// src/apiService.js — all backend API calls

export const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

/** Fetch a PDF from an authenticated endpoint and trigger a browser download. */
export function downloadPdf(path, filename) {
  const token = localStorage.getItem("id_token");
  return fetch(`${BASE_URL}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then(async res => {
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        let msg = "PDF generation failed";
        try { const j = JSON.parse(text); msg = j.error || msg; } catch {}
        throw new Error(msg);
      }
      return res.blob();
    })
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement("a");
      a.href    = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    })
    .catch(e => alert("Error: " + e.message));
}

// ── Auth helpers ──────────────────────────────────────────────────────────────
function getToken() { return localStorage.getItem("id_token"); }

async function api(method, path, body) {
  const headers = { "Content-Type": "application/json" };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${method} ${path} → ${res.status} ${text}`);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

const get    = path        => api("GET",    path);
const post   = (path, b)  => api("POST",   path, b);
const put    = (path, b)  => api("PUT",    path, b);
const patch  = (path, b)  => api("PATCH",  path, b);
const del    = (path, b)   => api("DELETE", path, b);

// ── Me ────────────────────────────────────────────────────────────────────────
export const fetchMe = () => get("/api/me");

// ── Raw Materials ─────────────────────────────────────────────────────────────
export const fetchRMs        = ()         => get("/api/rm");
export const fetchRMPrices   = ()         => get("/api/rm/prices");
export const createRM        = (body)     => post("/api/rm", body);
export const updateRMPrice   = (id, body) => post(`/api/rm/${id}/price`, body);

// ── SFGs ──────────────────────────────────────────────────────────────────────
export const fetchSFGs            = ()          => get("/api/sfg");
export const createSFG            = (body)      => post("/api/sfg", body);
export const fetchAllSFGFormulas  = ()          => get("/api/costing/formulations/sfg");
export const saveSFGFormula       = (id, lines) => put(`/api/formulations/SFG/${id}`, { components: lines });

// ── Finished Goods ────────────────────────────────────────────────────────────
export const fetchFGs             = ()          => get("/api/fg");
export const createFG             = (body)      => post("/api/fg", body);
export const fetchAllFGFormulas   = ()          => get("/api/costing/formulations/fg");
export const saveFGFormula        = (id, lines) => put(`/api/formulations/FG/${id}`, { components: lines });

// ── WB Price Snapshots ────────────────────────────────────────────────────────
export const getWbSnapshots    = ()          => get("/api/costing/wb-snapshots");
export const getWbSnapshot     = (id)        => get(`/api/costing/wb-snapshots/${id}`);
export const takeWbSnapshot    = (comment)   => post("/api/costing/wb-snapshots", { comment });

// ── Large Order Costing ───────────────────────────────────────────────────────
export const getLargeOrderCostings   = ()        => get("/api/costing/large-order-costings");
export const getLargeOrderCosting    = (id)      => get(`/api/costing/large-order-costings/${id}`);
export const createLargeOrderCosting = (body)    => post("/api/costing/large-order-costings", body);
export const updateLargeOrderCosting = (id,body) => put(`/api/costing/large-order-costings/${id}`, body);
export const deleteLargeOrderCosting = (id)      => del(`/api/costing/large-order-costings/${id}`);

// ── Global Settings ───────────────────────────────────────────────────────────
const PACKING_META = [
  { id: "pail20",   label: "20 kg Pail",    size: 20,   key: "packing20kg"   },
  { id: "drum200",  label: "200 kg Drum",   size: 200,  key: "packing200kg"  },
  { id: "ibc1000",  label: "1000 kg IBC",   size: 1000, key: "packing1000kg" },
];

export const fetchSettings = () =>
  get("/api/settings").then(s => ({
    labourPerKg:   Number(s.labourPerKg),
    wastageGlobal: Number(s.wastageGlobal),
    customPct:     Number(s.customPct),
    packing: PACKING_META.map(p => ({ ...p, price: Number(s[p.key]) })),
  }));

export const updateSettings = (settings) =>
  put("/api/settings", {
    labourPerKg:   settings.labourPerKg,
    wastageGlobal: settings.wastageGlobal,
    customPct:     settings.customPct,
    packing20kg:   settings.packing.find(p => p.id === "pail20")?.price,
    packing200kg:  settings.packing.find(p => p.id === "drum200")?.price,
    packing1000kg: settings.packing.find(p => p.id === "ibc1000")?.price,
  });

// ── RM Price History ──────────────────────────────────────────────────────────
export const fetchPriceHistory = ()         => get("/api/rm/price-history");

// ── Hot Melt ──────────────────────────────────────────────────────────────────
export const fetchHmGrades          = ()           => get("/api/hm/grades");
export const createHmGrade          = (body)       => post("/api/hm/grades", body);
export const updateHmGrade          = (id, body)   => put(`/api/hm/grades/${encodeURIComponent(id)}`, body);
export const fetchHmCycles          = ()           => get("/api/hm/cycles");
export const fetchHmCycle           = (slug)       => get(`/api/hm/cycles/${slug}`);
export const createHmCycle          = (body)       => post("/api/hm/cycles", body);
export const updateHmCycle          = (slug, body) => put(`/api/hm/cycles/${slug}`, body);
export const updateHmCycleSettings  = (slug, body) => put(`/api/hm/cycles/${slug}/settings`, body);

// ── Audit Log ─────────────────────────────────────────────────────────────────
export const fetchAuditLog = ({ entityType, actorEmail, from, to } = {}) => {
  const params = new URLSearchParams();
  if (entityType)  params.set("entityType",  entityType);
  if (actorEmail)  params.set("actorEmail",  actorEmail);
  if (from)        params.set("from",        from);
  if (to)          params.set("to",          to);
  const qs = params.toString();
  return get(`/api/audit${qs ? "?" + qs : ""}`);
};

// ── QC ────────────────────────────────────────────────────────────────────────
export const fetchFgSpecs       = ()            => get("/api/qc/fg-specs");
export const fetchFgSpec        = (fgId)        => get(`/api/qc/fg-specs/${encodeURIComponent(fgId)}`);
export const saveFgSpec         = (fgId, body)  => put(`/api/qc/fg-specs/${encodeURIComponent(fgId)}`, body);
export const fetchRmSpecs       = ()            => get("/api/qc/rm-specs");
export const fetchHmSpecs       = ()            => get("/api/qc/hm-specs");
export const fetchHmSpec        = (hmId)        => get(`/api/qc/hm-specs/${encodeURIComponent(hmId)}`);
export const saveHmSpec         = (hmId, body)  => put(`/api/qc/hm-specs/${encodeURIComponent(hmId)}`, body);
export const fetchRmSpec        = (rmId)        => get(`/api/qc/rm-specs/${encodeURIComponent(rmId)}`);
export const addRmSpecParam     = (rmId, body)  => post(`/api/qc/rm-specs/${encodeURIComponent(rmId)}`, body);
export const updateRmSpecParam  = (id, body)    => put(`/api/qc/rm-specs/params/${id}`, body);
export const deleteRmSpecParam  = (id)          => del(`/api/qc/rm-specs/params/${id}`);
export const fetchDailyAnalysis  = ()            => get("/api/qc/daily-analysis");
export const createDailyAnalysis = (body)        => post("/api/qc/daily-analysis", body);
export const deleteDailyAnalysis = (id)          => del(`/api/qc/daily-analysis/${id}`);
export const fetchHmAnalyses     = ()            => get("/api/qc/hm-analysis");
export const createHmAnalysis    = (body)        => post("/api/qc/hm-analysis", body);
export const deleteHmAnalysis    = (id)          => del(`/api/qc/hm-analysis/${id}`);
export const fetchRmAnalyses     = ()            => get("/api/qc/rm-analysis");
export const createRmAnalysis    = (body)        => post("/api/qc/rm-analysis", body);
export const deleteRmAnalysis    = (id)          => del(`/api/qc/rm-analysis/${id}`);

// ── Coolant Specs ─────────────────────────────────────────────────────────────
export const fetchCoolantSpecs  = ()                => get("/api/qc/coolant-specs");
export const saveCoolantSpec    = (productId, body) => put(`/api/qc/coolant-specs/${encodeURIComponent(productId)}`, body);

// ── Coolant Analysis ──────────────────────────────────────────────────────────
export const fetchCoolantAnalyses   = ()     => get("/api/qc/coolant-analysis");
export const createCoolantAnalysis  = (body) => post("/api/qc/coolant-analysis", body);
export const deleteCoolantAnalysis  = (id)   => del(`/api/qc/coolant-analysis/${id}`);

// ── BF Specs ──────────────────────────────────────────────────────────────────
export const fetchBfSpecs  = ()                => get("/api/qc/bf-specs");
export const saveBfSpec    = (productId, body) => put(`/api/qc/bf-specs/${encodeURIComponent(productId)}`, body);

// ── BF Analysis ───────────────────────────────────────────────────────────────
export const fetchBfAnalyses   = ()     => get("/api/qc/bf-analysis");
export const createBfAnalysis  = (body) => post("/api/qc/bf-analysis", body);
export const deleteBfAnalysis  = (id)   => del(`/api/qc/bf-analysis/${id}`);

// ── Water Analysis ────────────────────────────────────────────────────────────
export const fetchWaterAnalyses  = ()     => get("/api/qc/water-analysis");
export const createWaterAnalysis = (body) => post("/api/qc/water-analysis", body);
export const deleteWaterAnalysis = (id)   => del(`/api/qc/water-analysis/${id}`);

// ── RM Non-Conformance ────────────────────────────────────────────────────────
export const fetchRmNcs           = ()       => get("/api/qc/nc/rm");
export const createRmNc           = (body)   => post("/api/qc/nc/rm", body);
export const updateRmNcSupplier   = (id, b)  => put(`/api/qc/nc/rm/${id}/supplier`, b);
export const closeRmNc            = (id, b)  => put(`/api/qc/nc/rm/${id}/close`, b);
export const deleteRmNc           = (id)     => del(`/api/qc/nc/rm/${id}`);

// ── Customer Complaints ───────────────────────────────────────────────────────
export const fetchComplaints       = ()       => get("/api/qc/cc");
export const createComplaint       = (body)   => post("/api/qc/cc", body);
export const respondToComplaint    = (id, b)  => put(`/api/qc/cc/${id}/respond`, b);
export const closeComplaint        = (id)     => put(`/api/qc/cc/${id}/close`, {});
export const deleteComplaint       = (id)     => del(`/api/qc/cc/${id}`);

// ── FG Non-Conformance ────────────────────────────────────────────────────────
export const fetchFgNcs             = ()       => get("/api/qc/nc/fg");
export const createFgNc             = (body)   => post("/api/qc/nc/fg", body);
export const updateFgNcProduction   = (id, b)  => put(`/api/qc/nc/fg/${id}/production`, b);
export const closeFgNc              = (id, b)  => put(`/api/qc/nc/fg/${id}/close`, b);
export const deleteFgNc             = (id)     => del(`/api/qc/nc/fg/${id}`);

// ── Customer Samples ──────────────────────────────────────────────────────────
export const fetchCustomerSamples      = ()       => get("/api/qc/customer-samples");
export const createCustomerSample      = (body)   => post("/api/qc/customer-samples", body);
export const deleteCustomerSample      = (id)     => del(`/api/qc/customer-samples/${id}`);
export const fetchSampleSubmissions    = ()       => get("/api/qc/sample-submissions");
export const createSampleSubmission    = (body)   => post("/api/qc/sample-submissions", body);
export const updateSampleSubmission    = (id, body) => put(`/api/qc/sample-submissions/${id}`, body);

// ── Task Tracker ─────────────────────────────────────────────────────────────
export const fetchTasks       = ()              => get("/api/tasks");
export const createTask       = (body)          => post("/api/tasks", body);
export const updateTaskStatus = (id, status)    => put(`/api/tasks/${id}/status`, { status });
export const deleteTask       = (id)            => del(`/api/tasks/${id}`);
export const fetchComments    = (taskId)        => get(`/api/tasks/${taskId}/comments`);
export const addComment       = (taskId, body)  => post(`/api/tasks/${taskId}/comments`, body);

// ── COA ───────────────────────────────────────────────────────────────────────
export const fetchCoas    = ()        => get("/api/qc/coa");
export const createCoa    = (body)    => post("/api/qc/coa", body);
export const deleteCoa    = (id)      => del(`/api/qc/coa/${id}`);
export const fetchCoaLines = (id)     => get(`/api/qc/coa/${id}/lines`);

// ── TDS ───────────────────────────────────────────────────────────────────────
export const fetchTdsList    = ()      => get("/api/qc/tds");
export const fetchTdsHistory = (fgId)  => get(`/api/qc/tds/history/${encodeURIComponent(fgId)}`);
export const saveTds         = (body)  => post("/api/qc/tds", body);

// ── MSDS ──────────────────────────────────────────────────────────────────────
export const fetchMsdsList    = ()      => get("/api/qc/msds");
export const fetchMsdsHistory = (fgId)  => get(`/api/qc/msds/history/${encodeURIComponent(fgId)}`);
export const saveMsds         = (body)  => post("/api/qc/msds", body);

// ── R&D Pipeline ──────────────────────────────────────────────────────────────
export const fetchPipeline       = ()        => get("/api/rd/pipeline");
export const createPipelineProject = (body)  => post("/api/rd/pipeline", body);
export const updatePipelineProject = (id, b) => put(`/api/rd/pipeline/${id}`, b);
export const deletePipelineProject = (id)    => del(`/api/rd/pipeline/${id}`);

// ── NPD (New Product Development) ────────────────────────────────────────────
export const fetchNpdTrials  = ()         => get("/api/rd/npd");
export const fetchNpdDetail  = (id)       => get(`/api/rd/npd/${id}`);
export const createNpdTrial  = (body)     => post("/api/rd/npd", body);
export const updateNpdTrial  = (id, body) => put(`/api/rd/npd/${id}`, body);
export const deleteNpdTrial  = (id)       => del(`/api/rd/npd/${id}`);

// ── RM Evaluation ─────────────────────────────────────────────────────────────
export const fetchRmEvaluations  = ()         => get("/api/rd/rm-evaluation");
export const fetchRmEvalDetail   = (id)       => get(`/api/rd/rm-evaluation/${id}`);
export const createRmEvaluation  = (body)     => post("/api/rd/rm-evaluation", body);
export const updateRmEvaluation  = (id, body) => put(`/api/rd/rm-evaluation/${id}`, body);
export const deleteRmEvaluation  = (id)       => del(`/api/rd/rm-evaluation/${id}`);

// ── R&D Equipment ─────────────────────────────────────────────────────────────
export const fetchEquipment             = ()         => get("/api/rd/equipment");
export const createEquipment            = (body)     => post("/api/rd/equipment", body);
export const updateEquipment            = (id, b)    => put(`/api/rd/equipment/${id}`, b);
export const deleteEquipment            = (id)       => del(`/api/rd/equipment/${id}`);
export const fetchEquipmentCalibrations = (id)       => get(`/api/rd/equipment/${id}/calibrations`);
export const addCalibration             = (id, b)    => post(`/api/rd/equipment/${id}/calibrations`, b);
export const deleteCalibration          = (id)       => del(`/api/rd/equipment/calibrations/${id}`);
export const fetchAllCalibrations       = ()         => get("/api/rd/calibrations");
export const deleteCalibrationCertificate = (id)    => del(`/api/rd/equipment/calibrations/${id}/certificate`);

export async function uploadCalibrationCertificate(calibrationId, file) {
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${BASE_URL}/api/rd/equipment/calibrations/${calibrationId}/certificate`, {
    method: "POST", headers, body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
  return res.json();
}

// ── System Health ─────────────────────────────────────────────────────────────
export const fetchAppHealth    = ()     => get("/api/health");
export const fetchTallyPing    = ()     => get("/api/admin/tally/ping");
export const fetchTallyPingAmi = ()     => get("/api/admin/tally/ping/ami");
export const fetchTallyPingAmt = ()     => get("/api/admin/tally/ping/amt");

// ── CRM ───────────────────────────────────────────────────────────────────────
// ── Credit Applications ───────────────────────────────────────────────────────
export const fetchCreditApplications  = (accountId)  => get(`/api/crm/accounts/${accountId}/credit-applications`);
export const createCreditApplication  = (accountId, body) => post(`/api/crm/accounts/${accountId}/credit-applications`, body);
export const approveCreditApplication = (id)         => put(`/api/crm/credit-applications/${id}/approve`, {});
export const rejectCreditApplication  = (id, reason) => put(`/api/crm/credit-applications/${id}/reject`, { reason });
export const enterCreditApplication   = (id)         => put(`/api/crm/credit-applications/${id}/enter`, {});

export const fetchCrmCompanies      = ()          => get("/api/crm/companies");
export const fetchCrmAccounts       = ()          => get("/api/crm/accounts");
export const fetchCrmCustomers      = ()          => get("/api/crm/customers");
export const fetchCrmAccount        = (id)        => get(`/api/crm/accounts/${id}`);
export const createCrmAccount       = (body)      => post("/api/crm/accounts", body);
export const updateCrmAccount       = (id, body)  => put(`/api/crm/accounts/${id}`, body);
export const fetchAccountFinancial   = (id)        => get(`/api/crm/accounts/${id}/financial`);
export const fetchAccountOutstanding = (id)        => get(`/api/crm/accounts/${id}/outstanding`);

// Contacts
export const fetchAccountContacts   = (id)        => get(`/api/crm/accounts/${id}/contacts`);
export const createContact          = (accountId, body) => post(`/api/crm/accounts/${accountId}/contacts`, body);
export const updateContact          = (id, body)  => put(`/api/crm/contacts/${id}`, body);
export const deleteContact          = (id)        => del(`/api/crm/contacts/${id}`);

// Contact key dates
export const fetchKeyDates          = (contactId) => get(`/api/crm/contacts/${contactId}/key-dates`);
export const createKeyDate          = (contactId, body) => post(`/api/crm/contacts/${contactId}/key-dates`, body);
export const updateKeyDate          = (id, body)  => put(`/api/crm/key-dates/${id}`, body);
export const deleteKeyDate          = (id)        => del(`/api/crm/key-dates/${id}`);

// Account applications
export const fetchApplications      = (accountId) => get(`/api/crm/accounts/${accountId}/applications`);
export const createApplication      = (accountId, body) => post(`/api/crm/accounts/${accountId}/applications`, body);
export const updateApplication      = (id, body)  => put(`/api/crm/applications/${id}`, body);
export const deleteApplication      = (id)        => del(`/api/crm/applications/${id}`);

// Account products
export const fetchAccountProducts   = (accountId) => get(`/api/crm/accounts/${accountId}/products`);
export const createAccountProduct   = (accountId, body) => post(`/api/crm/accounts/${accountId}/products`, body);
export const deleteAccountProduct   = (id)        => del(`/api/crm/products/${id}`);

// Account machines
export const fetchMachines          = (accountId) => get(`/api/crm/accounts/${accountId}/machines`);
export const createMachine          = (accountId, body) => post(`/api/crm/accounts/${accountId}/machines`, body);
export const updateMachine          = (id, body)  => put(`/api/crm/machines/${id}`, body);
export const deleteMachine          = (id)        => del(`/api/crm/machines/${id}`);

// ── Quotations ────────────────────────────────────────────────────────────────
export const fetchQuotations         = ()           => get("/api/crm/quotations");
export const fetchQuotation          = (id)         => get(`/api/crm/quotations/${id}`);
export const fetchQuotationRevisions = (id)         => get(`/api/crm/quotations/${id}/revisions`);
export const createQuotation         = (body)       => post("/api/crm/quotations", body);
export const updateQuotation         = (id, body)   => put(`/api/crm/quotations/${id}`, body);
export const updateQuotationStatus   = (id, status) => put(`/api/crm/quotations/${id}/status`, { status });
export const deleteQuotation         = (id)         => del(`/api/crm/quotations/${id}`);
// PDF: use downloadPdf(`/api/crm/quotations/${id}/pdf`, `${quoNumber.replace(/\//g,'-')}.pdf`)


// ── Order Book ────────────────────────────────────────────────────────────────
export const fetchOrderBook        = (company = "AMI")           => get(`/api/crm/order-book?company=${company}`);
export const refreshOrderBook      = (company = "AMI")           => get(`/api/crm/order-book?company=${company}&refresh=true`);

// ── Production Plan ───────────────────────────────────────────────────────────
export const fetchProductionPlan   = (date)            => get(`/api/production-plan/${date}`);
export const saveProductionPlan    = (body)            => put(`/api/production-plan`, body);
export const fetchFgStock          = (refresh = false) => get(`/api/production-plan/fg-stock?refresh=${refresh}`);
export const suggestProductionPlan = (date)            => get(`/api/production-plan/suggest?date=${date}`);

// ── Mixers ────────────────────────────────────────────────────────────────────
export const fetchMixers    = ()           => get(`/api/mixers`);
export const updateMixer    = (id, body)  => put(`/api/mixers/${id}`, body);

// ── FG Mixer Assignments ──────────────────────────────────────────────────────
export const fetchFgMixers  = (fgId)           => get(`/api/fg/${fgId}/mixers`);
export const saveFgMixers   = (fgId, mixerIds) => put(`/api/fg/${fgId}/mixers`, mixerIds);

// ── SFG Mixer Assignments ─────────────────────────────────────────────────────
export const fetchSfgMixers  = (sfgId)            => get(`/api/sfg/${sfgId}/mixers`);
export const saveSfgMixers   = (sfgId, mixerIds)  => put(`/api/sfg/${sfgId}/mixers`, mixerIds);

// ── Delivery Plan ─────────────────────────────────────────────────────────────
export const fetchDeliveryPlan       = (date)         => get(`/api/delivery-plan/${date}`);
export const saveDeliveryPlan        = (body)         => put(`/api/delivery-plan`, body);
export const setDeliveryPlanStatus   = (date, status) => patch(`/api/delivery-plan/${date}/status`,   { status });
export const setProductionPlanStatus = (date, status) => patch(`/api/production-plan/${date}/status`, { status });

// ── Salesperson Groups ────────────────────────────────────────────────────────
export const loadCrmGroups         = ()                                      => get("/api/admin/crm/groups");
export const assignCrmGroup        = (companyId, groupName, userEmail)       => post("/api/admin/crm/groups/assign",        { companyId, groupName, userEmail });
export const ignoreCrmGroup        = (companyId, groupName)                  => post("/api/admin/crm/groups/ignore",        { companyId, groupName });
export const resetCrmGroup         = (companyId, groupName)                  => post("/api/admin/crm/groups/reset",         { companyId, groupName });
export const refreshCrmCache       = ()                                      => post("/api/admin/crm/groups/refresh-cache");
export const syncVoucherDates      = ()                                      => post("/api/admin/tally/sync-voucher-dates");
export const syncCreditData                  = ()           => post("/api/admin/tally/sync-credit");
export const snapshotCollectionForecast      = ()           => post("/api/admin/tally/snapshot-collection-forecast");
export const fetchMonthlyReport    = (year, month)                           => get(`/api/crm/monthly-report?year=${year}&month=${month}`);
export const refreshMonthlyReport  = (year, month)                           => post(`/api/crm/monthly-report/refresh?year=${year}&month=${month}`);
export const saveForecastBatch     = (year, month, userEmail, companyId, rows) =>
  put(`/api/crm/monthly-report/forecasts`, { year, month, userEmail, companyId, rows });
export const saveMonthlyTarget     = (year, month, userEmail, companyId, targetValue, targetQty) =>
  put(`/api/crm/monthly-report/target`, { year, month, userEmail, companyId, targetValue, targetQty });
export const fetchMonthlyReportComments  = (year, month)                  => get(`/api/crm/monthly-report/comments?year=${year}&month=${month}`);
export const addMonthlyReportComment     = (year, month, comment)         => post(`/api/crm/monthly-report/comments`, { year, month, comment });
export const fetchSalesTargets = (year)          => get(`/api/crm/sales-targets?year=${year}`);
export const saveSalesTargets  = (year, targets) => put(`/api/crm/sales-targets`, { year, targets });

// ── SOA / Payment Follow-up ───────────────────────────────────────────────────
export const fetchSoa   = ()  => get("/api/crm/soa");
export const refreshSoa = ()  => post("/api/crm/soa/refresh");

// ── Infrastructure Keys ───────────────────────────────────────────────────────
export const fetchInfraKeys  = ()      => get("/api/admin/infra-keys");
export const saveInfraKey    = (body)  => put("/api/admin/infra-keys", body);
export const deleteInfraKey  = (id)   => del(`/api/admin/infra-keys/${id}`);

// ── Bank Accounts ─────────────────────────────────────────────────────────────
export const fetchBankAccounts  = ()           => get("/api/crm/bank-accounts");
export const createBankAccount  = (body)       => post("/api/crm/bank-accounts", body);
export const updateBankAccount  = (id, body)   => put(`/api/crm/bank-accounts/${id}`, body);
export const deleteBankAccount  = (id)         => del(`/api/crm/bank-accounts/${id}`);

// ── Proforma Invoices ─────────────────────────────────────────────────────────
export const fetchProformaInvoices       = ()           => get("/api/crm/proforma-invoices");
export const fetchProformaInvoice        = (id)         => get(`/api/crm/proforma-invoices/${id}`);
export const fetchPiRevisions            = (id)         => get(`/api/crm/proforma-invoices/${id}/revisions`);
export const createProformaInvoice       = (body)       => post("/api/crm/proforma-invoices", body);
export const updateProformaInvoice       = (id, body)   => put(`/api/crm/proforma-invoices/${id}`, body);
export const updateProformaInvoiceStatus = (id, status) => put(`/api/crm/proforma-invoices/${id}/status`, { status });
export const deleteProformaInvoice       = (id)         => del(`/api/crm/proforma-invoices/${id}`);
// PDF: use downloadPdf(`/api/crm/proforma-invoices/${id}/pdf`, `${piNumber.replace(/\//g,'-')}.pdf`)

// ── Opportunities (Sales Pipeline) ───────────────────────────────────────────
export const fetchOpportunities        = ()         => get("/api/crm/opportunities");
export const fetchAccountOpportunities = (accountId)=> get(`/api/crm/accounts/${accountId}/opportunities`);
export const createOpportunity         = (body)     => post("/api/crm/opportunities", body);
export const updateOpportunity         = (id, body) => put(`/api/crm/opportunities/${id}`, body);
export const deleteOpportunity         = (id)       => del(`/api/crm/opportunities/${id}`);

// ── Sample Trials (Stage 52) ──────────────────────────────────────────────────
export const fetchAllTrials         = ()             => get("/api/crm/trials");
export const fetchOpportunityTrials = (oppId)        => get(`/api/crm/opportunities/${oppId}/trials`);
export const createTrial            = (oppId, body)  => post(`/api/crm/opportunities/${oppId}/trials`, body);
export const updateTrial            = (id, body)     => put(`/api/crm/trials/${id}`, body);
export const deleteTrial            = (id)           => del(`/api/crm/trials/${id}`);

// ── Visit Logs ────────────────────────────────────────────────────────────────
export const fetchVisitLogs       = (month, salesperson) =>
  get(`/api/crm/visit-logs?month=${month}${salesperson ? `&salesperson=${encodeURIComponent(salesperson)}` : ""}`);
export const fetchVisitLogSummary = (month, salesperson) =>
  get(`/api/crm/visit-logs/summary?month=${month}${salesperson ? `&salesperson=${encodeURIComponent(salesperson)}` : ""}`);
export const createVisitLog       = (body) => post("/api/crm/visit-logs", body);

// ── Work Orders ───────────────────────────────────────────────────────────────
export const fetchWorkOrders       = ()           => get("/api/crm/work-orders");
export const fetchWorkOrder        = (id)         => get(`/api/crm/work-orders/${id}`);
export const createWorkOrder       = (body)       => post("/api/crm/work-orders", body);
export const updateWorkOrderStatus = (id, status) => put(`/api/crm/work-orders/${id}/status`, { status });
export const deleteWorkOrder       = (id)         => del(`/api/crm/work-orders/${id}`);
export const deliverWorkOrder      = (id, body)   => post(`/api/crm/work-orders/${id}/deliver`, body);
export const fetchWOAttachments    = (id)         => get(`/api/crm/work-orders/${id}/attachments`);
export const deleteWOAttachment    = (id, aId)    => del(`/api/crm/work-orders/${id}/attachments/${aId}`);
export async function uploadWOAttachment(woId, file) {
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${BASE_URL}/api/crm/work-orders/${woId}/attachments`, {
    method: "POST", headers, body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Upload failed: ${res.status} ${text}`);
  }
  return res.json();
}

// ── User Management ───────────────────────────────────────────────────────────
export const fetchUsers    = ()                   => get("/api/users");
export const fetchRoles    = ()                   => get("/api/users/roles");
export const createUser    = (body)               => post("/api/users", body);
export const setUserActive  = (email, active)      => put(`/api/users/${encodeURIComponent(email)}/active`, { active });
export const updateUserName = (email, name)        => put(`/api/users/${encodeURIComponent(email)}/name`,   { name });
export const grantRole     = (email, roleCode)    => post(`/api/users/${encodeURIComponent(email)}/roles/${roleCode}`);
export const revokeRole    = (email, roleCode)    => del(`/api/users/${encodeURIComponent(email)}/roles/${roleCode}`);
